import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import ProductCard from '../components/products/ProductCard';
import { Link } from 'react-router-dom';
import { products, categories, siteImages, steps, CONTACT_INFO } from '../data/mock';
import { CheckCircle2, ArrowRight, Truck, ShieldCheck, RotateCcw, Music2, Award, Headphones } from 'lucide-react';

const HomePage = () => {
  const featuredProduct = products.find(p => p.slug === 'professional-concert-snare-drum') || products[0];
  const gridSlugs = [
    'full-size-viola',
    'handpan-steel-drum',
    'elite-concert-flute',
    'maestro-clarinet',
    'guitar-starter-set',
    'lyre-harp',
    'wood-color-record-player',
    'swan-harmonica-set',
  ];
  const productsGrid = gridSlugs.map(s => products.find(p => p.slug === s)).filter(Boolean);
  const [feature, ...restCats] = categories;

  useEffect(() => {
    document.title = 'Sound Peak Gear | Quality Musical Instruments & Music Gear';
  }, []);

  return (
    <div className="min-h-screen bg-white text-dark-800">
      <Header />
      <CartDrawer />

      <main>
        {/* Hero — full-bleed, centered */}
        <section className="relative bg-dark-950 overflow-hidden">
          <img src={siteImages.heroMain} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-dark-950/70 via-dark-950/80 to-dark-950" />
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 text-center">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 border border-athletic-500/40 text-athletic-400 text-xs font-bold uppercase tracking-[0.25em] rounded-full mb-6">
              <Music2 className="w-3.5 h-3.5" /> Built in the garage, tuned for the stage
            </span>
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold text-white uppercase tracking-wide leading-[1.02]">
              Find your <span className="text-athletic-500">sound</span>
            </h1>
            <p className="mt-6 text-lg text-dark-300 max-w-2xl mx-auto">
              Sound Peak Gear stocks a hand-picked lineup of instruments and music gear for players at every stage &mdash; from first strum to sold-out show. Free USA shipping on everything.
            </p>
            <div className="mt-9 flex flex-wrap gap-3 justify-center">
              <Link to="/shop" className="inline-flex items-center gap-2 rounded-md bg-athletic-500 px-8 py-4 font-semibold uppercase tracking-wide text-white transition hover:bg-athletic-600 shadow-button">
                Shop Instruments <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/shop?category=drums-percussion" className="inline-flex items-center gap-2 rounded-md border-2 border-white/25 px-8 py-4 font-semibold uppercase tracking-wide text-white hover:bg-white/10 transition">
                Explore Drums
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-8 text-sm text-dark-400 justify-center">
              <span className="flex items-center gap-2"><Truck className="w-4 h-4 text-athletic-500" /> Free USA shipping</span>
              <span className="flex items-center gap-2"><RotateCcw className="w-4 h-4 text-athletic-500" /> 30-day returns</span>
              <span className="hidden sm:flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-athletic-500" /> Secure checkout</span>
            </div>
          </div>
        </section>

        {/* Category showcase — asymmetric */}
        <section className="bg-white py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-8">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-athletic-600 mb-2">The collection</span>
                <h2 className="font-display text-3xl sm:text-4xl font-bold text-dark-900 uppercase">Pick your instrument</h2>
              </div>
              <Link to="/shop" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wide text-athletic-700 hover:gap-2.5 transition-all">
                View all <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {/* Feature (large) tile */}
              <Link
                to={`/shop?category=${feature.slug}`}
                className="group relative col-span-2 lg:row-span-2 h-64 lg:h-full min-h-[280px] rounded-md overflow-hidden bg-dark-900"
              >
                <img src={feature.image} alt={feature.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-950 via-dark-950/50 to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-end p-6">
                  <span className="text-athletic-400 text-xs font-bold uppercase tracking-[0.2em]">{feature.productCount} products</span>
                  <h3 className="font-display text-2xl sm:text-3xl font-bold text-white uppercase mt-1">{feature.name}</h3>
                  <p className="text-dark-300 text-sm mt-2 max-w-sm">{feature.description}</p>
                </div>
              </Link>
              {restCats.map((category) => (
                <Link
                  key={category.id}
                  to={`/shop?category=${category.slug}`}
                  className="group relative h-52 rounded-md overflow-hidden bg-dark-900"
                >
                  <img src={category.image} alt={category.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-110 transition-all duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-950 via-dark-950/40 to-transparent" />
                  <div className="absolute inset-0 flex flex-col justify-end p-4">
                    <h3 className="font-display text-lg font-bold text-white uppercase">{category.name}</h3>
                    <p className="text-xs text-athletic-400 mt-0.5">{category.productCount} product{category.productCount > 1 ? 's' : ''}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Best sellers grid */}
        <section className="bg-dark-50 py-16 sm:py-20 border-y border-dark-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-athletic-600 mb-2">On the workbench</span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-dark-900 uppercase">Players' favorites</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {productsGrid.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
            <div className="text-center mt-12">
              <Link to="/shop" className="inline-flex items-center gap-2 px-7 py-3.5 bg-dark-900 text-white font-semibold uppercase tracking-wide rounded-md hover:bg-athletic-600 transition">
                Browse all instruments <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Flagship spotlight */}
        <section className="relative overflow-hidden bg-dark-950">
          <img src={siteImages.ctaBackground} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-dark-950 via-dark-950/85 to-dark-950/40" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
            <div className="order-2 lg:order-1">
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-athletic-500 mb-3">Flagship spotlight</span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-4 uppercase">{featuredProduct.name}</h2>
              <p className="text-dark-300 mb-6 max-w-lg">{featuredProduct.description.slice(0, 175)}&hellip;</p>
              <p className="text-3xl font-bold text-athletic-500 mb-7 font-display">${featuredProduct.price.toFixed(2)}</p>
              <div className="flex flex-wrap gap-3">
                <Link to={`/product/${featuredProduct.slug}`} className="inline-flex items-center gap-2 px-7 py-3.5 bg-athletic-500 text-white rounded-md font-semibold uppercase tracking-wide hover:bg-athletic-600 transition">Buy now</Link>
                <Link to="/shop" className="inline-flex items-center gap-2 px-7 py-3.5 border-2 border-white/20 text-white rounded-md font-medium uppercase tracking-wide hover:bg-white/10 transition">Browse more</Link>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative max-w-md mx-auto">
                <img src={featuredProduct.image} alt={featuredProduct.name} className="w-full rounded-md bg-white p-6" />
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="bg-white py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-athletic-600 mb-2">How it works</span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-dark-900 uppercase">From cart to concert</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((step) => (
                <div key={step.id} className="relative bg-dark-50 border border-dark-100 rounded-md p-6">
                  <span className="font-display text-5xl font-bold text-athletic-500/25">0{step.id}</span>
                  <h3 className="font-display text-lg font-bold text-dark-900 uppercase mt-2">{step.title}</h3>
                  <p className="text-dark-600 text-sm mt-2">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Sound Peak */}
        <section className="bg-dark-50 py-16 sm:py-20 border-y border-dark-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
              <img className="h-80 w-full lg:h-[440px] lg:max-w-md rounded-md object-cover shadow-elevated flex-shrink-0" src={siteImages.heroLifestyle} alt="Inside the Sound Peak Gear workshop" />
              <div>
                <span className="inline-block px-3 py-1 bg-athletic-500/10 text-athletic-700 text-xs font-bold uppercase tracking-[0.2em] rounded-full mb-3">Why Sound Peak</span>
                <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold uppercase text-dark-900">
                  Gear worth <span className="text-athletic-600">playing</span>
                </h2>
                <p className="mt-5 text-lg text-dark-600">
                  We skip the endless walls of lookalike gear and hand-pick a small lineup we would actually put in our own cases &mdash; then back every one with free shipping and easy returns.
                </p>
                <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {['Handpicked, playable stock', 'Clean intonation & setup', 'Beginner to gigging players', 'Real support, real people', 'Fast free USA shipping', 'Honest, upfront pricing'].map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-dark-700">
                      <CheckCircle2 className="h-5 w-5 flex-none text-athletic-600 mt-0.5" /> <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-9 flex flex-wrap gap-6">
                  <div className="flex items-center gap-2.5 text-dark-800"><Award className="w-6 h-6 text-athletic-600" /> <span className="text-sm font-semibold">Quality guaranteed</span></div>
                  <div className="flex items-center gap-2.5 text-dark-800"><Headphones className="w-6 h-6 text-athletic-600" /> <span className="text-sm font-semibold">Player-first support</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-white py-16 sm:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-3xl font-bold text-dark-900 text-center mb-10 uppercase">Frequently asked questions</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[
                { q: 'What is the typical shipping time?', a: `Your order is processed and shipped via USPS within 1 business day. Delivery usually lands within 2 to 4 business days. For delays, contact us at ${CONTACT_INFO.phone}.` },
                { q: 'Is shipping free?', a: 'Yes — every order ships with free USPS Standard Shipping within the USA, no minimum required.' },
                { q: 'How do returns work?', a: 'You have 30 days from delivery to start a return. Just reach out to our team and we will walk you through it.' },
                { q: 'How do I reach support?', a: `Our team is available at ${CONTACT_INFO.phone} or by email at ${CONTACT_INFO.email}.` },
              ].map((faq, i) => (
                <div key={i} className="bg-dark-50 rounded-md p-6 border border-dark-100">
                  <h3 className="font-display text-lg font-semibold text-dark-900 uppercase">{faq.q}</h3>
                  <p className="mt-2 text-dark-600 normal-case">{faq.a}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/faq" className="inline-flex items-center gap-2 text-athletic-700 hover:text-athletic-800 font-semibold uppercase tracking-wide">See all FAQs <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
